import { useState, useEffect, useRef, useCallback, useId } from 'react';

/**
 * HistorySuggestInput
 *
 * A text input that shows history-derived suggestions from /field-suggestions.
 * Suggestions appear immediately on focus and update with each keystroke.
 * Matching is case-insensitive; displayed values are title-cased.
 *
 * Props:
 *   field       — field key understood by /field-suggestions (e.g. 'fert_product')
 *   name        — HTML name attribute for the input
 *   value       — controlled value
 *   onChange    — (e) => void  — same signature as a regular input onChange
 *   placeholder — input placeholder text
 *   className   — extra class names for the input element
 *   disabled    — disable the input
 */
function HistorySuggestInput({
  field,
  name,
  value,
  onChange,
  placeholder = '',
  className = '',
  disabled = false,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const listboxId = useId();
  const debounceTimer = useRef(null);
  const containerRef = useRef(null);
  const hasInteracted = useRef(false);

  const fetchSuggestions = useCallback((q) => {
    const params = new URLSearchParams({ field, q: q || '' });
    fetch(`/field-suggestions?${params}`)
      .then(r => r.json())
      .then(data => {
        setSuggestions(data.results || []);
        setOpen((data.results || []).length > 0);
        setActiveIndex(-1);
      })
      .catch(() => setSuggestions([]));
  }, [field]);

  // Debounce while typing
  useEffect(() => {
    if (!hasInteracted.current) return;
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => fetchSuggestions(value), 200);
    return () => clearTimeout(debounceTimer.current);
  }, [fetchSuggestions, value]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (suggestion) => {
    // Fire a synthetic event matching the standard onChange signature
    onChange({ target: { name, value: suggestion } });
    setSuggestions([]);
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && open && suggestions.length > 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex >= 0 ? activeIndex : 0]);
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, -1));
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div className="autocomplete-wrapper" ref={containerRef}>
      <input
        type="text"
        name={name}
        className={`form-input ${className}`}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        onChange={(e) => {
          hasInteracted.current = true;
          onChange(e);
        }}
        onFocus={() => {
          hasInteracted.current = true;
          fetchSuggestions(value);
        }}
        onBlur={() => {
          setTimeout(() => {
            setSuggestions([]);
            setOpen(false);
          }, 150);
        }}
        onKeyDown={handleKeyDown}
        aria-autocomplete="list"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        role="combobox"
      />

      {open && suggestions.length > 0 && (
        <ul id={listboxId} className="autocomplete-dropdown" role="listbox">
          {suggestions.map((s, i) => (
            <li
              key={i}
              role="option"
              aria-selected={i === activeIndex}
              className={`autocomplete-option${i === activeIndex ? ' autocomplete-option-active' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(s);
              }}
            >
              <span className="autocomplete-option-label">{s}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default HistorySuggestInput;
